import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle, Warning, XCircle, MagnifyingGlass, ArrowSquareOut, Megaphone, ChartPieSlice } from "@phosphor-icons/react";
import { toast } from "sonner";
import PhotoLightbox from "@/components/PhotoLightbox";

const STATUS_META = {
  published: { color: "#00873D", label: "Publicado", Icon: CheckCircle },
  expired:   { color: "#FF8A00", label: "Restock",   Icon: Warning },
  missing:   { color: "#E0000F", label: "Faltante",  Icon: XCircle },
};

export default function PublicationsOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all|missing|expired|published
  const [sellerFilter, setSellerFilter] = useState("all");
  const [lb, setLb] = useState({ open: false, photos: [], title: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/publications/overview");
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al cargar resumen");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filteredRows = useMemo(() => {
    if (!data) return [];
    const t = search.trim().toLowerCase();
    return data.rows
      .filter((r) => !t || (r.product_name || "").toLowerCase().includes(t) || (r.brand || "").toLowerCase().includes(t))
      .filter((r) => {
        if (statusFilter === "all") return true;
        // Row passes if any cell matches the status (or with seller filter, only that seller's cell)
        const cells = sellerFilter === "all" ? r.cells : r.cells.filter((c) => c.seller_id === sellerFilter);
        return cells.some((c) => c.status === statusFilter);
      });
  }, [data, search, statusFilter, sellerFilter]);

  const visibleSellers = useMemo(() => {
    if (!data) return [];
    if (sellerFilter === "all") return data.sellers;
    return data.sellers.filter((s) => s.id === sellerFilter);
  }, [data, sellerFilter]);

  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short" }); } catch { return ""; } };

  if (loading || !data) {
    return <div className="p-6 sm:p-8 text-zinc-500">Cargando…</div>;
  }

  const totalCells = data.total_publicable * (sellerFilter === "all" ? data.sellers.length : 1);
  const aggPublished = data.seller_totals
    .filter((s) => sellerFilter === "all" || s.seller_id === sellerFilter)
    .reduce((acc, s) => acc + s.published, 0);
  const aggExpired = data.seller_totals
    .filter((s) => sellerFilter === "all" || s.seller_id === sellerFilter)
    .reduce((acc, s) => acc + s.expired, 0);
  const aggMissing = totalCells - aggPublished - aggExpired;
  const coveragePct = totalCells ? Math.round((aggPublished / totalCells) * 100) : 0;

  return (
    <div className="p-6 sm:p-8 space-y-6" data-testid="pub-overview-page">
      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">MARKETPLACE</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight">Publicaciones · Resumen</h1>
          <p className="text-sm text-zinc-500 mt-1">Quién publicó qué. Detecta huecos por vendedor y restocks pendientes de re-publicar.</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <Select value={sellerFilter} onValueChange={setSellerFilter}>
            <SelectTrigger className="w-48 h-9 text-xs" data-testid="pub-ov-seller-filter"><SelectValue placeholder="Vendedor"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los vendedores</SelectItem>
              {data.sellers.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44 h-9 text-xs" data-testid="pub-ov-status-filter"><SelectValue placeholder="Estatus"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estatus</SelectItem>
              <SelectItem value="missing">Faltantes</SelectItem>
              <SelectItem value="expired">Restock pendiente</SelectItem>
              <SelectItem value="published">Publicadas</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative w-60">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"/>
            <Input placeholder="Buscar producto…" value={search} onChange={(e)=>setSearch(e.target.value)} className="pl-9 h-9" data-testid="pub-ov-search"/>
          </div>
        </div>
      </header>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="pub-ov-kpis">
        <div className="border border-zinc-200 p-3 bg-white">
          <div className="text-[10px] uppercase font-mono-data tracking-wider text-zinc-500">Productos publicables</div>
          <div className="numeric text-2xl font-bold mt-1">{data.total_publicable}</div>
          <div className="text-[10px] text-zinc-400">con foto y stock</div>
        </div>
        <div className="border border-zinc-200 p-3 bg-white">
          <div className="text-[10px] uppercase font-mono-data tracking-wider text-zinc-500">Faltantes</div>
          <div className="numeric text-2xl font-bold mt-1 text-[#E0000F]">{aggMissing}</div>
        </div>
        <div className="border border-zinc-200 p-3 bg-white">
          <div className="text-[10px] uppercase font-mono-data tracking-wider text-zinc-500">Restock pendiente</div>
          <div className="numeric text-2xl font-bold mt-1 text-[#FF8A00]">{aggExpired}</div>
        </div>
        <div className="border border-zinc-200 p-3 bg-white">
          <div className="text-[10px] uppercase font-mono-data tracking-wider text-zinc-500">Cobertura</div>
          <div className="numeric text-2xl font-bold mt-1 text-[#00873D]">{coveragePct}%</div>
          <div className="text-[10px] text-zinc-400">{aggPublished} de {totalCells}</div>
        </div>
      </div>

      {/* Sellers leaderboard */}
      <section data-testid="pub-ov-sellers" className="space-y-2">
        <h2 className="font-heading text-lg font-bold flex items-center gap-2"><ChartPieSlice size={18}/> Por vendedor</h2>
        <div className="border border-zinc-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
              <tr>
                <th className="text-left px-4 py-2.5">Vendedor</th>
                <th className="text-center px-4 py-2.5">Publicadas</th>
                <th className="text-center px-4 py-2.5">Restock</th>
                <th className="text-center px-4 py-2.5">Faltantes</th>
                <th className="text-right px-4 py-2.5">Cobertura</th>
              </tr>
            </thead>
            <tbody>
              {data.seller_totals.map((s) => {
                const cov = s.total_publicable ? Math.round((s.published / s.total_publicable) * 100) : 0;
                return (
                  <tr key={s.seller_id} className="border-t border-zinc-100">
                    <td className="px-4 py-2.5 font-medium">{s.seller_name}</td>
                    <td className="px-4 py-2.5 text-center numeric font-mono-data text-[#00873D] font-semibold">{s.published}</td>
                    <td className="px-4 py-2.5 text-center numeric font-mono-data text-[#FF8A00] font-semibold">{s.expired}</td>
                    <td className="px-4 py-2.5 text-center numeric font-mono-data text-[#E0000F] font-semibold">{s.missing}</td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="inline-flex items-center gap-2">
                        <div className="w-24 h-2 bg-zinc-100 rounded overflow-hidden">
                          <div className="h-full" style={{ width: `${cov}%`, background: cov >= 80 ? "#00873D" : cov >= 50 ? "#FFC000" : "#E0000F" }}/>
                        </div>
                        <span className="numeric font-mono-data text-xs font-semibold w-9 text-right">{cov}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {data.seller_totals.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-zinc-400 text-xs">Sin vendedores.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Matrix */}
      <section data-testid="pub-ov-matrix" className="space-y-2">
        <h2 className="font-heading text-lg font-bold flex items-center gap-2"><Megaphone size={18}/> Matriz por producto</h2>
        <div className="border border-zinc-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase text-zinc-500 sticky top-0">
              <tr>
                <th className="text-left px-4 py-2.5 sticky left-0 bg-zinc-50 z-10 min-w-[260px]">Producto</th>
                {visibleSellers.map((s) => (
                  <th key={s.id} className="text-center px-2 py-2.5 whitespace-nowrap">{s.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 && (
                <tr><td colSpan={visibleSellers.length + 1} className="px-4 py-10 text-center text-zinc-400 text-xs">Sin resultados.</td></tr>
              )}
              {filteredRows.map((r) => (
                <tr key={r.product_id} className="border-t border-zinc-100 hover:bg-zinc-50/50">
                  <td className="px-4 py-2 sticky left-0 bg-white z-10">
                    <div className="flex items-center gap-2.5">
                      <button type="button" onClick={() => r.photo && setLb({ open: true, photos: [r.photo], title: r.product_name })} className="w-10 h-10 border border-zinc-200 rounded overflow-hidden bg-zinc-50 flex items-center justify-center flex-shrink-0">
                        {r.photo ? <img src={r.photo} alt="" className="w-full h-full object-cover"/> : <span className="text-[8px] text-zinc-400">sin</span>}
                      </button>
                      <div className="min-w-0">
                        <div className="font-medium text-xs leading-tight" title={r.product_name}>{r.product_name}</div>
                        <div className="text-[10px] text-zinc-500 mt-0.5">{[r.brand, r.category].filter(Boolean).join(" · ") || "—"} · <span className="font-mono-data">{r.stock}</span></div>
                        {r.last_restock_at && <div className="text-[9px] text-[#FF8A00] mt-0.5">Restock {fmtDate(r.last_restock_at)}</div>}
                      </div>
                    </div>
                  </td>
                  {visibleSellers.map((s) => {
                    const cell = r.cells.find((c) => c.seller_id === s.id);
                    const meta = STATUS_META[cell?.status || "missing"];
                    return (
                      <td key={s.id} className="px-2 py-2 text-center" title={`${meta.label}${cell?.published_at ? " · " + fmtDate(cell.published_at) : ""}`}>
                        {cell?.marketplace_url ? (
                          <a href={cell.marketplace_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-8 h-8 rounded hover:bg-zinc-100" style={{ color: meta.color }}>
                            <meta.Icon size={20} weight="fill"/>
                          </a>
                        ) : (
                          <span className="inline-flex items-center justify-center w-8 h-8" style={{ color: meta.color }}>
                            <meta.Icon size={20} weight={cell?.status === "missing" ? "regular" : "fill"}/>
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap gap-3 text-[11px] text-zinc-500">
          <span className="inline-flex items-center gap-1"><CheckCircle size={14} weight="fill" color="#00873D"/> Publicado</span>
          <span className="inline-flex items-center gap-1"><Warning size={14} weight="fill" color="#FF8A00"/> Restock — debe republicar</span>
          <span className="inline-flex items-center gap-1"><XCircle size={14} color="#E0000F"/> Faltante</span>
          <span className="inline-flex items-center gap-1"><ArrowSquareOut size={12}/> click en el ícono abre el post</span>
        </div>
      </section>

      <PhotoLightbox open={lb.open} onClose={()=>setLb((s)=>({...s, open:false}))} photos={lb.photos} title={lb.title} filenameBase={lb.title} />
    </div>
  );
}
